<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import EventSidebarSection from './EventSidebarSection.vue';

const props = defineProps({
  formattedTotal: { type: String, default: null },
  formattedSuggested: { type: String, default: null },
  formattedImpact: { type: String, default: null },
  impactDelta: { type: Number, default: null },
  loading: { type: Boolean, default: false },
  // Main amount mode: accepted quote, open issued quotes, or live suggested.
  priceMode: {
    type: String,
    default: 'suggested',
    validator: value => ['accepted', 'quoted', 'suggested'].includes(value),
  },
  // When true, never render a real amount — only a fixed blurred placeholder.
  redacted: { type: Boolean, default: false },
});

const { t } = useI18n();
const EMPTY_VALUE = '—';
const REDACTED_PLACEHOLDER = '••••••';

const title = computed(() => {
  if (props.priceMode === 'accepted') return t('PLANNING.SHOW.ACCEPTED_PRICE');
  if (props.priceMode === 'quoted') return t('PLANNING.SHOW.QUOTED_PRICE');
  return t('PLANNING.SHOW.LIVE_PRICING');
});
</script>

<template>
  <EventSidebarSection :title="title" icon="i-lucide-receipt-text">
    <div class="flex flex-col gap-1">
      <span
        v-if="redacted"
        class="select-none text-3xl font-bold tracking-tight text-n-slate-9 blur-sm"
        aria-hidden="true"
      >
        {{ REDACTED_PLACEHOLDER }}
      </span>
      <span
        v-else-if="loading && !formattedTotal"
        class="inline-block h-9 w-28 animate-pulse rounded bg-n-slate-4"
        aria-hidden="true"
      />
      <span
        v-else
        class="text-3xl font-bold tracking-tight"
        :class="
          formattedTotal
            ? 'text-woot-600 dark:text-woot-300'
            : 'text-n-slate-9'
        "
      >
        {{ formattedTotal || EMPTY_VALUE }}
      </span>
      <span
        v-if="!redacted && !loading && !formattedTotal"
        class="text-xs text-n-slate-10"
      >
        {{ t('PLANNING.SHOW.PRICING_EMPTY') }}
      </span>
      <span
        v-if="!redacted && formattedSuggested"
        class="text-xs text-slate-500"
      >
        {{ t('PLANNING.SHOW.LIVE_PRICING') }}: {{ formattedSuggested }}
      </span>
      <span
        v-if="!redacted && formattedImpact"
        class="text-sm font-medium"
        :class="impactDelta > 0 ? 'text-red-500' : 'text-emerald-500'"
      >
        {{ formattedImpact }}
      </span>
    </div>
  </EventSidebarSection>
</template>
